/**
 * UNIT-BASED VARIANT PICKER JAVASCRIPT
 * Handles products with 3 options: Size + Unit + Color
 */

class VariantPickerUnitBased {
  constructor() {
    if (!window.vpubProductData) {
      console.error('Product data not found');
      return;
    }

    this.data = window.vpubProductData;
    this.selectedUnit = null;
    this.selectedColor = null;
    this.quantities = {};

    this.init();
  }

  init() {
    console.log('✓ Unit-Based Variant Picker initialized');
    console.log('Product data:', this.data);

    // Setup event listeners
    this.setupUnitSelection();
    this.setupColorSelection();
    this.setupAddToCart();

    // Auto-select first unit if only one
    if (this.data.options.unit.values.length === 1) {
      const firstBtn = document.querySelector('.unit-btn');
      if (firstBtn) firstBtn.click();
    }
  }

  // ==========================================
  // UNIT SELECTION
  // ==========================================
  setupUnitSelection() {
    const unitBtns = document.querySelectorAll('.unit-btn');

    unitBtns.forEach(btn => {
      btn.addEventListener('click', () => this.handleUnitSelect(btn));
    });
  }

  handleUnitSelect(btn) {
    const unit = btn.dataset.unit;

    console.log('Unit selected:', unit);

    // Update UI
    document.querySelectorAll('.unit-btn').forEach(b => 
      b.classList.remove('selected')
    );
    btn.classList.add('selected');

    // Store selection
    this.selectedUnit = unit;

    // Update display
    const display = document.getElementById('selected-unit-display');
    if (display) {
      display.textContent = unit;
    }

    // Reset color and table
    this.resetColorSelection();
    this.clearSizeTable();
    this.updateCartButton();
  }

  resetColorSelection() {
    document.querySelectorAll('.color-swatch-btn').forEach(btn => 
      btn.classList.remove('selected')
    );
    this.selectedColor = null;

    const display = document.getElementById('selected-color-display');
    if (display) {
      display.textContent = `Select ${this.data.options.color.name}`;
    }
  }

  // ==========================================
  // COLOR SELECTION
  // ==========================================
  setupColorSelection() {
    const colorBtns = document.querySelectorAll('.color-swatch-btn');

    colorBtns.forEach(btn => {
      btn.addEventListener('click', () => this.handleColorSelect(btn));
    });
  }

  handleColorSelect(btn) {
    if (!this.selectedUnit) {
      alert(`Please select ${this.data.options.unit.name} first`);
      return;
    }

    const color = btn.dataset.color;

    console.log('Color selected:', color);

    // Update UI
    document.querySelectorAll('.color-swatch-btn').forEach(b => 
      b.classList.remove('selected')
    );
    btn.classList.add('selected');

    // Store selection
    this.selectedColor = color;

    // Update display
    const display = document.getElementById('selected-color-display');
    if (display) {
      display.textContent = color;
    }

    // Generate size table
    this.generateSizeTable();
    this.updateCartButton();
  }

  // ==========================================
  // SIZE TABLE GENERATION
  // ==========================================
  generateSizeTable() {
    console.log('Generating size table for:', {
      unit: this.selectedUnit,
      color: this.selectedColor
    });

    const container = document.getElementById('size-table-container');
    if (!container) return;

    // Find matching variants
    const variants = this.findMatchingVariants(
      this.selectedUnit,
      this.selectedColor
    );

    console.log('Found variants:', variants.length);

    if (variants.length === 0) {
      container.innerHTML = '<div class="placeholder-message">No variants found for this combination</div>';
      return;
    }

    // Create table
    let html = `
      <table class="size-table">
        <thead>
          <tr>
            <th>Size</th>
            <th>Price</th>
            <th>Stock</th>
            <th>Quantity</th>
          </tr>
        </thead>
        <tbody>
    `;

    variants.forEach(variant => {
      const size = this.getSizeFromVariant(variant);
      const stockText = variant.available 
        ? (variant.inventory > 0 ? `${variant.inventory}+` : '999+')
        : 'Out of Stock';
      const stockClass = variant.available ? '' : 'out-of-stock';

      html += `
        <tr>
          <td class="size-name-cell">${size}</td>
          <td class="price-cell">$${(variant.price / 100).toFixed(2)}</td>
          <td class="stock-cell ${stockClass}">${stockText}</td>
          <td>
            <input type="number" 
                   class="qty-input" 
                   data-variant-id="${variant.id}"
                   min="0" 
                   value="0"
                   ${variant.available ? '' : 'disabled'}>
          </td>
        </tr>
      `;
    });

    html += `
        </tbody>
      </table>
    `;

    container.innerHTML = html;

    // Setup quantity input listeners
    this.setupQuantityInputs();
  }

  findMatchingVariants(unit, color) {
    const unitIdx = this.data.options.unit.index;
    const colorIdx = this.data.options.color.index;

    return this.data.variants.filter(variant => {
      const varUnit = variant[`option${unitIdx + 1}`];
      const varColor = variant[`option${colorIdx + 1}`];

      return varUnit === unit && varColor === color;
    });
  }

  getSizeFromVariant(variant) {
    const sizeIdx = this.data.options.size.index;
    return variant[`option${sizeIdx + 1}`];
  }

  // ==========================================
  // QUANTITY MANAGEMENT
  // ==========================================
  setupQuantityInputs() {
    const inputs = document.querySelectorAll('.qty-input');

    inputs.forEach(input => {
      input.addEventListener('change', () => this.handleQuantityChange(input));
      input.addEventListener('input', () => this.handleQuantityChange(input));
    });
  }

  handleQuantityChange(input) {
    const variantId = parseInt(input.dataset.variantId);
    const qty = parseInt(input.value) || 0;

    if (qty > 0) {
      this.quantities[variantId] = qty;
    } else {
      delete this.quantities[variantId];
    }

    console.log('Quantities updated:', this.quantities);

    this.updateCartButton();
    this.updateCartSummary();
  }

  updateCartSummary() {
    const summary = document.getElementById('cart-summary');
    const totalQtyEl = document.getElementById('total-qty');

    const totalQty = Object.values(this.quantities).reduce((sum, qty) => sum + qty, 0);

    if (totalQty > 0) {
      totalQtyEl.textContent = totalQty;
      summary.style.display = 'block';
    } else {
      summary.style.display = 'none';
    }
  }

  // ==========================================
  // ADD TO CART
  // ==========================================
  setupAddToCart() {
    const btn = document.getElementById('vpub-add-cart');
    if (!btn) return;

    btn.addEventListener('click', () => this.addToCart());
  }

  updateCartButton() {
    const btn = document.getElementById('vpub-add-cart');
    if (!btn) return;

    const hasQuantities = Object.keys(this.quantities).length > 0;
    btn.disabled = !hasQuantities;
  }

  async addToCart() {
    const btn = document.getElementById('vpub-add-cart');
    const btnText = btn.querySelector('.btn-text');
    const btnLoading = btn.querySelector('.btn-loading');

    // Prepare items
    const items = Object.entries(this.quantities).map(([variantId, quantity]) => ({
      id: parseInt(variantId),
      quantity: quantity
    }));

    if (items.length === 0) {
      alert('Please enter quantities');
      return;
    }

    console.log('Adding to cart:', items);

    // Show loading
    btn.disabled = true;
    btnText.style.display = 'none';
    btnLoading.style.display = 'inline-flex';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      if (response.ok) {
        this.showMessage(`✓ Added ${items.length} item(s) to cart!`, 'success');

        // Clear quantities
        this.quantities = {};
        document.querySelectorAll('.qty-input').forEach(input => {
          input.value = 0;
        });
        this.updateCartSummary();
        this.updateCartButton();

        // Trigger cart update event
        document.dispatchEvent(new CustomEvent('cart:updated'));

      } else {
        const error = await response.json();
        throw new Error(error.description || 'Failed to add to cart');
      }

    } catch (error) {
      console.error('Cart error:', error);
      this.showMessage('Error: ' + error.message, 'error');
    } finally {
      // Reset button
      btn.disabled = false;
      btnText.style.display = 'inline';
      btnLoading.style.display = 'none';
    }
  }

  clearSizeTable() {
    const container = document.getElementById('size-table-container');
    if (container) {
      container.innerHTML = `
        <div class="placeholder-message">
          Please select ${this.data.options.unit.name} and ${this.data.options.color.name} first
        </div>
      `;
    }

    this.quantities = {};
    this.updateCartSummary();
  }

  showMessage(message, type) {
    // Remove existing messages
    document.querySelectorAll('.vpub-toast').forEach(el => el.remove());

    const toast = document.createElement('div');
    toast.className = `vpub-toast vpub-toast-${type}`;
    toast.textContent = message;

    Object.assign(toast.style, {
      position: 'fixed',
      top: '20px',
      right: '20px',
      padding: '15px 25px',
      borderRadius: '8px',
      background: type === 'success' ? '#10b981' : '#ef4444',
      color: 'white',
      fontWeight: '600',
      fontSize: '14px',
      zIndex: '99999',
      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
      animation: 'slideIn 0.3s ease'
    });

    // Add animation styles
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideIn {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
      @keyframes slideOut {
        from { transform: translateX(0); opacity: 1; }
        to { transform: translateX(100%); opacity: 0; }
      }
    `;
    document.head.appendChild(style);

    document.body.appendChild(toast);

    // Auto remove after 4 seconds
    setTimeout(() => {
      toast.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => toast.remove(), 300);
    }, 4000);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (document.querySelector('.variant-picker-unit-based')) {
      window.vpubPicker = new VariantPickerUnitBased();
    }
  });
} else {
  if (document.querySelector('.variant-picker-unit-based')) {
    window.vpubPicker = new VariantPickerUnitBased();
  }
}
